<template>
  <section :class="['px-8 lg:px-[80px] py-[50px] md:pb-24', removeDesktopPaddingTop ? 'md:pt-0' : 'md:pt-24']">
    <div class="max-w-content mx-auto flex flex-col gap-12">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5">
        <div class="flex flex-col gap-2 items-center sm:items-start">
          <h2 :class="['text-[30px] md:text-[36px] font-semibold text-grey-dark-50 leading-tight', { 'text-grey-light-900': !isDarkBackground } ]">
            Latest blog posts
          </h2>
          <p :class="['text-base md:text-xl text-grey-dark-300 text-center sm:text-left', { 'text-grey-light-600': !isDarkBackground }]">
            Stay up-to-date with LayUp’s latest news and resources.
          </p>
        </div>
        <router-link :to="blogPage">
          <Button size="business" class="cursor-pointer w-full sm:w-auto">
            View all posts
          </Button>
        </router-link>
      </div>
      <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        <div v-for="n in 3" :key="n" class="flex flex-col gap-4">
          <Skeleton class="w-full h-52 rounded-xl" />
          <Skeleton class="w-1/3 h-4 rounded" />
          <Skeleton class="w-full h-6 rounded" />
          <Skeleton class="w-2/3 h-4 rounded" />
        </div>
      </div>
      <div v-else class="w-full">
        <BlogGrid :posts="posts" :is-dark-background="isDarkBackground" :show-posts-from-index="0" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import Skeleton from '@/components/ui/skeleton/SkeletonComponent.vue';
import { Button } from '@/components/ui/button';
import { BUSINESS_BLOG_TAG_ID } from '@/config.ts';
import { fetchRecentPosts, type BlogPost } from '@/services/blogService.ts';
import { addBreadcrumb, createWarning } from '@/utils/monitoring.utils.ts';
import { onMounted, ref } from 'vue';
import BlogGrid from '@/components/BlogGrid.vue';
import { computed } from 'vue';

const posts = ref<BlogPost[]>([]);
const isLoading = ref(true);

interface Props {
  isDarkBackground?: boolean;
  blogTagId?: string;
  removeDesktopPaddingTop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  blogTagId: BUSINESS_BLOG_TAG_ID,
  isDarkBackground: false,
  removeDesktopPaddingTop: true,
});

const blogPage = computed(() => {
  return props.blogTagId === BUSINESS_BLOG_TAG_ID ? { name: 'business-blog' } : { name: 'shopper-blog' }
});

onMounted(async () => {
  try {
    const response = await fetchRecentPosts(1, 3, props.blogTagId, false);
    posts.value = response.posts;
  } catch (error) {
    addBreadcrumb('blog.preview.load', 'Failed to load latest posts');
    createWarning(error);
  } finally {
    isLoading.value = false;
  }
});
</script>
